import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../../../components/form";
import { AppActionsRow, AppFormField, AppFormGrid, PageSectionPanel } from "../../../components/layout";
import { formControlLabelAlignSx, formRowButtonSx, GRID_FULL } from "../../../lib/ui/formLayout";
import {
  adminOpenSearchPolicyService,
  type OpenSearchIndexPolicySettings,
} from "../../../services/adminOpenSearchPolicyService";

type Props = {
  clusterIndices: string[];
};

export default function AdminOpenSearchPolicyTab({ clusterIndices }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<OpenSearchIndexPolicySettings>({
    enforceAllowlist: false,
    allowedIndices: [],
    allowedPrefixes: [],
  });
  const [prefixInput, setPrefixInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminOpenSearchPolicyService.getSettings();
      setSettings({
        enforceAllowlist: res.enforceAllowlist,
        allowedIndices: res.allowedIndices ?? [],
        allowedPrefixes: res.allowedPrefixes ?? [],
        lastModifiedDate: res.lastModifiedDate,
        lastModifiedBy: res.lastModifiedBy,
      });
      setPrefixInput((res.allowedPrefixes ?? []).join(", "));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const prefixes = prefixInput
        .split(/[,\n]/)
        .map((value) => value.trim())
        .filter(Boolean);
      const saved = await adminOpenSearchPolicyService.saveSettings({
        enforceAllowlist: settings.enforceAllowlist,
        allowedIndices: settings.allowedIndices,
        allowedPrefixes: prefixes,
      });
      setSettings({
        enforceAllowlist: saved.enforceAllowlist,
        allowedIndices: saved.allowedIndices ?? [],
        allowedPrefixes: saved.allowedPrefixes ?? [],
        lastModifiedDate: saved.lastModifiedDate,
        lastModifiedBy: saved.lastModifiedBy,
      });
      setPrefixInput((saved.allowedPrefixes ?? []).join(", "));
      setSuccess(t("adminOpenSearchPolicy.saved"));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography color="text.secondary">{t("common.loading")}</Typography>;
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Alert severity="info">{t("adminOpenSearchPolicy.hint")}</Alert>

      <PageSectionPanel>
        <AppFormGrid>
          <AppFormField size={GRID_FULL}>
            <FormControlLabel
              sx={formControlLabelAlignSx}
              control={
                <Switch
                  size="small"
                  checked={settings.enforceAllowlist}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, enforceAllowlist: e.target.checked }))
                  }
                />
              }
              label={t("adminOpenSearchPolicy.enforceAllowlist")}
            />
          </AppFormField>

          <AppFormField size={GRID_FULL}>
            <Autocomplete
              multiple
              freeSolo
              options={clusterIndices}
              value={settings.allowedIndices}
              onChange={(_, value) =>
                setSettings((prev) => ({
                  ...prev,
                  allowedIndices: value.map((item) => item.trim()).filter(Boolean),
                }))
              }
              disabled={!settings.enforceAllowlist}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return <Chip key={key} {...chipProps} label={option} size="small" />;
                })
              }
              renderInput={(params) => (
                <AppTextField
                  {...params}
                  label={t("adminOpenSearchPolicy.allowedIndices")}
                  helperText={t("adminOpenSearchPolicy.allowedIndicesHelp")}
                />
              )}
            />
          </AppFormField>

          <AppFormField size={GRID_FULL}>
            <AppTextField
              label={t("adminOpenSearchPolicy.allowedPrefixes")}
              helperText={t("adminOpenSearchPolicy.allowedPrefixesHelp")}
              value={prefixInput}
              onChange={(e) => setPrefixInput(e.target.value)}
              disabled={!settings.enforceAllowlist}
              multiline
              minRows={2}
            />
          </AppFormField>

          {settings.lastModifiedDate && (
            <AppFormField size={GRID_FULL}>
              <Typography variant="caption" color="text.secondary">
                {t("adminOpenSearchPolicy.lastUpdated", {
                  date: new Date(settings.lastModifiedDate).toLocaleString(),
                  user: settings.lastModifiedBy || "—",
                })}
              </Typography>
            </AppFormField>
          )}

          <AppFormField size={GRID_FULL}>
            <AppActionsRow align="start">
              <AppButton variant="contained" onClick={onSave} disabled={saving} sx={formRowButtonSx}>
                {saving ? t("common.saving") : t("common.save")}
              </AppButton>
              <AppButton variant="outlined" onClick={() => load()} disabled={saving} sx={formRowButtonSx}>
                {t("common.refresh")}
              </AppButton>
            </AppActionsRow>
          </AppFormField>
        </AppFormGrid>
      </PageSectionPanel>
    </Stack>
  );
}
